import * as React from "react";
import { ChatMessage } from "../../types/chatTypes";
import { Folder, FileText, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { extractDocumentIds, processDocumentReferences, processImageReferences, DocumentInfo as DocumentReferenceInfo } from "../../utils/document-resolver";
import InlineImage from "../atoms/InlineImage";
import { getDocumentsInfo, generateDocumentUrl, DocumentInfo } from "./thoughtRenderer/toolResults/documentHelpers";
import { getPillProps } from "../../utils/documentStyles";
import { ChatPillData } from "../../types/chatTypes";

interface MentionPillProps extends ChatPillData {
  onViewDocument?: (url: string, name: string) => void;
}

const MentionPill = ({ id, name, type, isCategory, url, onViewDocument }: MentionPillProps) => {
  const { styles, iconPath, label } = getPillProps({ id, name, type, isCategory });

  const handleClick = () => {
    if (url) {
      if (onViewDocument) {
        onViewDocument(url, name || 'Document');
      } else {
        window.open(url, '_blank');
      }
    } else if (id) {
      console.log("Opening document:", id);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md transition-all border shadow-sm",
        "text-[12px] font-medium group mx-0.5 align-baseline",
        styles.bg,
        styles.text,
        styles.border,
        styles.hover
      )}
      title={url ? "Click to open document" : name}
    >
      <img src={iconPath} className="h-3.5 w-3.5 object-contain opacity-80 group-hover:opacity-100 transition-opacity" alt="" />
      <span className="truncate max-w-[200px]">{name}</span>
      {url && <ExternalLink className="h-3 w-3 opacity-50 group-hover:opacity-100 ml-0.5" />}
    </button>
  );
};

interface UserMessageProps {
  message: ChatMessage;
  onViewDocument?: (url: string, name: string) => void;
}

const UserMessage: React.FC<UserMessageProps> = ({ message, onViewDocument }) => {
  const [documents, setDocuments] = React.useState<DocumentReferenceInfo[]>([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = React.useState(false);

  // Extraer IDs de documentos del contenido usando el mismo patrón que MarkdownMessage
  const documentIds = React.useMemo(() => {
    return extractDocumentIds(message.content || '');
  }, [message.content]);

  // Cargar información de documentos cuando se detecten UUIDs (y pills kb-* desde adjuntos)
  React.useEffect(() => {
    const loadDocuments = async () => {
      const kbIdsInText = documentIds.filter((id) => id.startsWith("kb-"));
      const fromKbAnchors: DocumentReferenceInfo[] = kbIdsInText.map((id) => {
        const att = message.attachments?.find((a: { id: string }) => a.id === id);
        return {
          id,
          name: att?.name || "Knowledge base",
          type: att ? (att as { type?: string }).type : "knowledge_base",
        };
      });

      const fileUuids = documentIds.filter((id) => !id.startsWith("kb-"));

      if (fileUuids.length === 0 && fromKbAnchors.length === 0) {
        setDocuments([]);
        return;
      }

      setIsLoadingDocuments(true);
      try {
        const docs = fileUuids.length > 0 ? await getDocumentsInfo(fileUuids) : [];
        
        // Generar URLs para documentos que no las tengan
        const docsWithUrls: DocumentReferenceInfo[] = await Promise.all(
          docs.map(async (doc) => {
            let finalUrl = doc.pdf_url;
            
            // Si tenemos source_path pero no pdf_url, generar la URL
            if (!finalUrl && doc.source_path) {
              finalUrl = await generateDocumentUrl(doc.source_path);
            }

            return {
              id: doc.id,
              name: doc.name,
              type: doc.document_type,
              url: finalUrl || undefined
            };
          })
        );

        const merged = [...fromKbAnchors];
        const seen = new Set(merged.map((d) => d.id));
        for (const d of docsWithUrls) {
          if (!seen.has(d.id)) {
            seen.add(d.id);
            merged.push(d);
          }
        }
        setDocuments(merged);
      } catch (error) {
        console.error('Error loading documents:', error);
        setDocuments(fromKbAnchors);
      } finally {
        setIsLoadingDocuments(false);
      }
    };

    loadDocuments();
  }, [documentIds.join(','), message.content, JSON.stringify((message.attachments || []).map((a) => a.id))]);
  
  const { renderedContent, remainingAttachments } = React.useMemo(() => {
    const content = message.content || '';
    const attachments = message.attachments || [];

    // Primero, procesar imágenes inline y obtener un array mixto (strings + nodos React)
    const imageParts = processImageReferences(content, (url, alt, idx) => (
      <InlineImage
        key={`inline-img-${idx}`}
        src={url}
        alt={alt || `image-${idx}`}
        className="max-w-[280px] max-h-[400px]"
      />
    ));

    const renderedParts: React.ReactNode[] = [];
    const renderedIds = new Set<string>();

    const hasExecutionTemplateMention = /\(execution_template:/i.test(content);

    // PRIORIDAD 1: Referencias a docs / KB / execution templates en el texto
    if ((documentIds.length > 0 && documents.length > 0) || hasExecutionTemplateMention) {
      imageParts.forEach((part, outerIdx) => {
        if (typeof part === 'string') {
          const parts = processDocumentReferences(
            part,
            documents.length > 0 ? documents : [],
            (docId, docName, docType) => {
              renderedIds.add(docId);
              const isKb = docId.startsWith("kb-");
              const isEt = docType === "execution_template";
              return (
                  <MentionPill 
                    key={`uuid-pill-${docId}-${outerIdx}`}
                    id={docId}
                    name={docName}
                    type={isEt ? "template" : isKb ? "knowledge_base" : "document"}
                    isCategory={isKb}
                    url={documents.find(d => d.id === docId)?.url}
                    onViewDocument={onViewDocument}
                  />
              );
            }
          );

          parts.forEach((p, i) => {
            if (typeof p === 'string') {
              renderedParts.push(<span key={`text-${outerIdx}-${i}`} className="whitespace-pre-wrap">{p}</span>);
            } else {
              renderedParts.push(<React.Fragment key={`badge-${outerIdx}-${i}`}>{p}</React.Fragment>);
            }
          });
        } else {
          // Nodo React (ej. imagen inline)
          renderedParts.push(part);
        }
      });

    } else {
      // PRIORIDAD 2: Fallback al matching por nombre (retrocompatibilidad), aplicado por segmento de texto
      if (attachments.length === 0) {
        return { 
          renderedContent: <span className="whitespace-pre-wrap">{imageParts}</span>, 
          remainingAttachments: [] 
        };
      }

      const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      };

      const sortedAttachments = [...attachments].sort((a, b) => b.name.length - a.name.length);
      const patternString = sortedAttachments.map(doc => escapeRegExp(doc.name)).join('|');

      imageParts.forEach((part, outerIdx) => {
        if (typeof part === 'string') {
          if (!patternString) {
            renderedParts.push(<span key={`text-${outerIdx}`} className="whitespace-pre-wrap">{part}</span>);
            return;
          }

          const pattern = new RegExp(`(${patternString})`, 'g');
          const parts = part.split(pattern);

          parts.forEach((sub, subIdx) => {
            const matchedDoc = sortedAttachments.find(doc => doc.name === sub);
            if (matchedDoc) {
              renderedIds.add(matchedDoc.id);
              renderedParts.push(
                  <MentionPill 
                    key={`pill-${outerIdx}-${subIdx}`}
                    id={matchedDoc.id}
                    name={sub}
                    type={(matchedDoc as any).type}
                    isCategory={(matchedDoc as any).isCategory}
                    url={(matchedDoc as any).url}
                    onViewDocument={onViewDocument}
                  />
              );
            } else {
              renderedParts.push(<span key={`text-${outerIdx}-${subIdx}`} className="whitespace-pre-wrap">{sub}</span>);
            }
          });

        } else {
          renderedParts.push(part);
        }
      });
    }

    const remaining = attachments.filter(doc => !renderedIds.has(doc.id));

    return { 
      renderedContent: <span className="whitespace-pre-wrap">{renderedParts}</span>, 
      remainingAttachments: remaining 
    };
  }, [message.content, message.attachments, documentIds, documents]);

  return (
    <div className="w-full mb-4">
      <div className="flex flex-col items-end">
        {/* Message Bubble */}
        <div className="bg-slate-50 text-slate-900 px-4 py-3 rounded-xl rounded-br-sm border border-slate-200 shadow-sm max-w-[85%] w-fit text-sm leading-relaxed break-words">
          {/* Main content with inline pills */}
          <div>
            {renderedContent}
          </div>
          
          {/* Remaining attachments */}
          {remainingAttachments.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-200/60 flex flex-wrap gap-1.5">
              {remainingAttachments.map((doc) => (
                doc.type === 'image' && (doc as any).url ? (
                  <InlineImage
                    key={doc.id}
                    src={(doc as any).url}
                    alt={doc.name}
                    className="max-w-[280px] max-h-[400px]"
                  />
                ) : (
                  <MentionPill 
                    key={doc.id}
                    id={doc.id}
                    name={doc.name}
                    type={(doc as any).type}
                    isCategory={(doc as any).isCategory}
                    url={(doc as any).url}
                    onViewDocument={onViewDocument}
                  />
                )
              ))}
            </div>
          )}
        </div>
        
        {/* Timestamp */}
        <div className="text-[10px] text-gray-400 mt-1 mr-1">
          {message.timestamp.toLocaleTimeString('es-ES', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false
          })}
        </div>
      </div>
    </div>
  );
};

export default UserMessage;
